import type { AlertClientResponse } from "../../clients/types";
import { Integration } from "../../types";
import { sigv4ApiClient } from "./internal/sigv4ApiClient";
import { createEntityId } from "./internal/entityId";

const API_PATH = "/v1/integration";

type IntegrationApiPayload = Omit<Integration, "alertSchemaId"> & {
  alertSchemaId?: string;
  alarmSchemaId?: string;
};

const normalizeIntegration = (integration: IntegrationApiPayload): Integration => {
  const { alarmSchemaId, ...rest } = integration;
  const alertSchemaId =
    typeof rest.alertSchemaId === "string" && rest.alertSchemaId.trim().length > 0
      ? rest.alertSchemaId
      : typeof alarmSchemaId === "string"
        ? alarmSchemaId
        : "";

  return {
    ...rest,
    alertSchemaId,
  };
};

const toApiPayload = (integration: Record<string, unknown>): Record<string, unknown> => {
  const { alertSchemaId, ...rest } = integration;
  return {
    ...rest,
    alarmSchemaId: alertSchemaId ?? rest.alarmSchemaId,
  };
};

const readSetupOAuthUrl = (payload: unknown): string => {
  if (typeof payload === "string") {
    return payload;
  }

  if (
    typeof payload === "object" &&
    payload !== null &&
    "url" in payload &&
    typeof payload.url === "string"
  ) {
    return payload.url;
  }

  throw new Error("OAuth setup response is missing redirect URL.");
};

export const integrationService = {
  create: async (integration: Omit<Integration, "url" |  "id">): Promise<Integration> => {
    const id = createEntityId();
    const created = {...integration, id: id, url: `https://${id}.oversee.com`};
    const payload = await sigv4ApiClient.put<IntegrationApiPayload>(API_PATH, toApiPayload(created));
    return normalizeIntegration(payload);
  },

  list: async (): Promise<Integration[]> => {
    const payload = await sigv4ApiClient.list<IntegrationApiPayload>(API_PATH);
    return payload.map(normalizeIntegration);
  },

  get: async (id: string): Promise<Integration | undefined> => {
    const payload = await sigv4ApiClient.get<IntegrationApiPayload>(
      `${API_PATH}/${encodeURIComponent(id)}`,
    );
    return payload ? normalizeIntegration(payload) : undefined;
  },

  patch: async (
    id: string,
    patch: Partial<Omit<Integration, "id">>
  ): Promise<Integration> => {
    const current = await integrationService.get(id);
    if (!current) {
      throw new Error(`Integration with id "${id}" not found`);
    }

    if (current.system) {
      const readonlyFields: Array<keyof Omit<Integration, "id" | "auth" | "metadata">> = [
        "name",
        "alertSchemaId",
        "description",
        "system",
        "url",
      ];

      const hasReadonlyViolation = readonlyFields.some((field) => {
        if (!Object.prototype.hasOwnProperty.call(patch, field)) {
          return false;
        }
        return patch[field] !== current[field];
      });

      if (hasReadonlyViolation) {
        throw new Error(
          "System integrations are read-only. Only setup fields can be updated.",
        );
      }
    }

    const payload = await sigv4ApiClient.put<IntegrationApiPayload>(
      API_PATH,
      toApiPayload({ ...current, ...patch, id }),
    );
    return normalizeIntegration(payload);
  },

  delete: async (id: string): Promise<boolean> => {
    const current = await integrationService.get(id);
    if (!current) {
      return false;
    }
    if (current.system) {
      return false;
    }
    await sigv4ApiClient.delete(`${API_PATH}/${encodeURIComponent(id)}`);
    return true;
  },

  setupOAuth: async (id: string, region: string): Promise<string> => {
    const payload = await sigv4ApiClient.getRaw<unknown>(
      "POST",
      `${API_PATH}/${encodeURIComponent(id)}/setup`,
      {
        query: { region },
        body: {},
      },
    );

    return readSetupOAuthUrl(payload);
  },

  listAlarms: async (integrationId: string): Promise<AlertClientResponse[]> => {
    return sigv4ApiClient.list<AlertClientResponse>(
      `${API_PATH}/${encodeURIComponent(integrationId)}/alarm`,
    );
  },
};
